(ns client-portal.client.asr-training.data
  (:require [client-portal.client.concepts.data :as concepts.d]
            [client-portal.client.smart-ordering.filters.data]
            [client-portal.client.smart-ordering.sessions.data :as so.sessions.d]
            [clojure.string :as s]
            [shared.core.reactive :as re]
            [shared.core.router :as router]
            [shared.core.time :as time]
            [shared.core.util :as util]))

(def product-configs
  {"smart-ordering" {:base-query-args so.sessions.d/base-query-args}})

(def required-listening-percent 0)

(def root-path [:asr-training])
(def panel-key :asr-training/panel)
(def page-size 100)

(def settings-path (conj root-path :settings))
(def mode-path (conj settings-path :mode))
(def playback-speed-path (conj settings-path :playback-speed))
(def auto-play-path (conj settings-path :auto-play?))
(def hide-original-transcript-path (conj settings-path :hide-original-transcript?))
(def settings-form-path (conj root-path :settings-form))

(def list-mode "list")
(def utterance-mode "utterance")

(defn mode [db]
  (get-in (:local-storage db) mode-path list-mode))

(re/reg-sub :asr-training/mode- mode)

(defn sync-panel! [product mode]
  (re/dispatch (if (= utterance-mode mode)
                 [:layout/show-panel-right [panel-key {:product product}]]
                 [:layout/hide-panel-right])))

(re/reg-event-fx
  :asr-training/set-mode re/middleware
  (fn [{:keys [local-storage]} [product mode]]
    (sync-panel! product mode)
    {:local-storage (assoc-in local-storage mode-path mode)}))

(defn playback-speed [db]
  (get-in (:local-storage db) playback-speed-path 1))

(re/reg-sub :asr-training/playback-speed- playback-speed)

(defn auto-play? [db]
  (get-in (:local-storage db) auto-play-path true))

(defn hide-original-transcript? [db]
  (get-in (:local-storage db) hide-original-transcript-path false))

(re/reg-sub :asr-training/hide-original-transcript?- hide-original-transcript?)

(re/reg-event-db
  :asr-training/open-settings re/middleware
  (fn [db [product]]
    (re/dispatch [:dialog/show-with-form-init
                  [:asr-training/settings-dialog {:product product}]
                  settings-form-path
                  {:playback-speed (playback-speed db)
                   :auto-play? (auto-play? db)
                   :hide-original-transcript? (hide-original-transcript? db)}])
    db))

(defn list-path [product]
  (conj root-path product :list))

(defn filters-config [product]
  {:root-path (conj root-path product)
   :reload-event [:asr-training/reload product]})

(defn search [db]
  (some-> db (router/current-query-params) :search (s/trim) (not-empty)))

(re/reg-sub :asr-training/search- search)

(def replace-form-path (conj root-path :replace-form))

(defn replacement-path [product]
  (conj root-path product :replacement))

(defn replacement [db product]
  (get-in db (replacement-path product)))

(re/reg-sub
  :asr-training/replacement-
  (fn [db [_ product]]
    (replacement db product)))

(defn auto-replace [transcript search-text replacement-text]
  (cond-> transcript
          (and search-text replacement-text) (util/replace-all-ignore-case search-text replacement-text)))

(defn default-transcript [db product transcript]
  (auto-replace transcript (search db) (replacement db product)))

(re/reg-sub
  :asr-training/default-transcript-
  (fn [[_ product]]
    [(re/subscribe [:asr-training/search-])
     (re/subscribe [:asr-training/replacement- product])])
  (fn [[search-text replacement-text] [_ _ transcript]]
    (auto-replace transcript search-text replacement-text)))

(re/reg-event-db
  :asr-training/open-replace re/middleware
  (fn [db [product]]
    (re/dispatch [:dialog/show-with-form-init
                  [:asr-training/replace-dialog {:product product}]
                  replace-form-path
                  {:replacement (replacement db product)}])
    db))

(re/reg-event-db
  :asr-training/save-replacement re/middleware
  (fn [db [product]]
    (let [v (get-in db (conj replace-form-path :replacement))]
      (if (s/blank? v)
        db
        (do
          (re/dispatch [:dialog/hide])
          (assoc-in db (replacement-path product) v))))))

(re/reg-event-db
  :asr-training/clear-replacement re/middleware
  (fn [db [product]]
    (re/dispatch [:dialog/hide])
    (assoc-in db (replacement-path product) nil)))

(re/reg-event-db
  :asr-training/set-search re/middleware
  (fn [db [product v]]
    (let [v (some-> v (s/trim) (not-empty))]
      (if (= v (search db))
        db
        (do
          (re/dispatch [:router/go-with-merged-query-params {:search v}])
          (re/dispatch [:asr-training/reload product])
          (assoc-in db (replacement-path product) nil))))))

(defn header-height-path [product]
  (conj root-path product :header-height))

(re/reg-sub
  :asr-training/header-height-
  (fn [db [_ product]]
    (get-in db (header-height-path product) 0)))

(re/reg-event-db
  :asr-training/set-header-height re/middleware
  (fn [db [product h]]
    (assoc-in db (header-height-path product) h)))

(defn edits-path [product]
  (conj root-path product :edits))

(defn submissions-path [product]
  (conj root-path product :submissions))

(defn completed-path [product]
  (conj root-path product :completed))

(defn utterance-key [{:keys [conversation-id start]}]
  (str conversation-id "|" start))

(defn page-path [product page]
  (conj (list-path product) page))

(defn current-max-page [db product]
  (some->> (get-in db (list-path product))
           (util/when-not-loading)
           (keys)
           (apply max)))

(defn has-more? [db product]
  (some->> (get (util/when-not-loading (get-in db (list-path product))) (current-max-page db product))
           (util/when-not-loading)
           (count)
           (<= page-size)))

(re/reg-sub
  :asr-training/has-more?-
  (fn [db [_ product]]
    (has-more? db product)))

(defn list-loading? [db product]
  (let [list-data (get-in db (list-path product))]
    (or (util/loading? list-data) (some-> list-data (get 0) (util/loading?)))))

(re/reg-sub
  :asr-training/list-loading?-
  (fn [db [_ product]]
    (list-loading? db product)))

(defn next-page-loading? [db product]
  (let [page (current-max-page db product)]
    (and (pos? page) (util/loading? (get-in db (page-path product page))))))

(re/reg-sub
  :asr-training/next-page-loading?-
  (fn [db [_ product]]
    (next-page-loading? db product)))

(defn next-page [db product]
  (some-> (current-max-page db product) (inc)))

(re/reg-sub
  :asr-training/next-page-
  (fn [db [_ product]]
    (next-page db product)))

(re/reg-auth0-loader
  :asr-training/load-utterances
  (fn [[[_ {:keys [product page]}]] _]
    (page-path product (or page 0)))
  (fn [db [_ {:keys [product page]} _]]
    (let [{:keys [base-query-args]} (get product-configs product)]
      (when (and base-query-args
                 (not (next-page-loading? db product))
                 (not (list-loading? db product))
                 (concepts.d/current-concept-id db))
        (let [last-loaded (some-> (util/when-not-loading (get-in db (list-path product)))
                                  (get (or (dec (or page 0)) 0))
                                  (last))]
          {:method :post
           :url "/api/asr-training/utterances/list"
           :json-params (merge (base-query-args db {:end-date (:conversation-ts-raw last-loaded)
                                                    :limit page-size})
                               {:product product
                                :end-date-start-ms (:start last-loaded)
                                :end-date-conversation-id (:conversation-id last-loaded)
                                :search (search db)})}))))
  :transf (fn [resp]
            (or (some->> resp
                         :body
                         (util/->kebab-case-keywords)
                         (mapv #(assoc %
                                  :conversation-ts-raw (:conversation-ts %)
                                  :conversation-ts (time/reformat-date-str (:conversation-ts %) "M/d/yy h:mm A")
                                  :offset (some-> (:start %) (/ 1000) (js/Math.round) (time/seconds->format)))))
                []))
  :error-transf (fn [] []))

(defn audio-path [product]
  (conj root-path product :audio))

(defonce audio-context* (atom nil))
(defonce audio-buffers* (atom {}))
(defonce audio-source* (atom nil))
(defonce audio-buffer* (atom nil))
(defonce anchor* (atom {:ctx-time 0
                         :offset 0
                         :speed 1}))
(defonce position* (re/ratom 0))
(defonce listened* (re/ratom 0))

(defn audio-context []
  (or @audio-context*
      (reset! audio-context* (js/AudioContext.))))

(defn segment-duration [{:keys [start end]}]
  (-> end (- start) (/ 1000) (max 0)))

(defn current-position [utterance]
  (if @audio-source*
    (let [{:keys [ctx-time offset speed]} @anchor*]
      (-> (audio-context) (.-currentTime) (- ctx-time) (* speed) (+ offset) (min (segment-duration utterance))))
    @position*))

(defn re-anchor! [offset speed]
  (reset! anchor* {:ctx-time (.-currentTime (audio-context))
                   :offset offset
                   :speed speed}))

(defn stop-audio! []
  (when-let [^js source @audio-source*]
    (set! (.-onended source) nil)
    (.stop source)
    (reset! audio-source* nil)))

(defn clear-playback! []
  (stop-audio!)
  (reset! audio-buffer* nil)
  (reset! position* 0)
  (reset! listened* 0))

(defn listening-required? []
  (pos? required-listening-percent))

(defn listened-enough? [utterance]
  (or (not (listening-required?))
      (>= @listened* (-> utterance (segment-duration) (* required-listening-percent) (/ 100)))))

(defn pause-audio! [utterance]
  (reset! position* (current-position utterance))
  (stop-audio!))

(defn conversation-audio-buffer [db product {:keys [conversation-id dataset-id audio-url]}]
  (or (get @audio-buffers* conversation-id)
      (let [api-get (fn [access-token path query]
                      (js/fetch (str path "?" (-> query (util/remove-nil-from-map) (clj->js) (js/URLSearchParams.)))
                                #js {:headers #js {"authorization" (str "Bearer " access-token)}}))
            fetch-audio #(api-get %1 "/api/asr-training/conversation/audio" {:product product
                                                                              :audio-url %2})
            buffer-promise (-> ((:get-access-token-silently (re/auth0-details db)))
                               (.then (fn [access-token]
                                        (-> (fetch-audio access-token audio-url)
                                            (.then #(if (= 410 (.-status %))
                                                      (-> (api-get access-token
                                                                   "/api/asr-training/conversation/audio-url"
                                                                   {:concept-id (concepts.d/current-concept-id db)
                                                                    :product product
                                                                    :conversation-id conversation-id
                                                                    :dataset-id dataset-id})
                                                          (.then (fn [resp] (.json resp)))
                                                          (.then (fn [^js body] (fetch-audio access-token (.-audioUrl body)))))
                                                      %)))))
                               (.then #(if (.-ok %)
                                         (.arrayBuffer %)
                                         (throw (js/Error. "Failed to load audio"))))
                               (.then #(.decodeAudioData (audio-context) %)))]
        (.catch buffer-promise #(swap! audio-buffers* dissoc conversation-id))
        (swap! audio-buffers* assoc conversation-id buffer-promise)
        buffer-promise)))

(defn play-left-channel! [product {:keys [start] :as utterance} offset speed]
  (stop-audio!)
  (let [^js buffer @audio-buffer*
        ctx (audio-context)
        source (.createBufferSource ctx)
        splitter (.createChannelSplitter ctx (.-numberOfChannels buffer))
        duration (segment-duration utterance)
        offset (if (< offset duration) offset 0)
        k (utterance-key utterance)]
    (set! (.-buffer source) buffer)
    (set! (.. source -playbackRate -value) speed)
    (.connect source splitter)
    (.connect splitter (.-destination ctx) 0)
    (set! (.-onended source) #(do
                                (reset! audio-source* nil)
                                (swap! listened* + (max 0 (- duration @position*)))
                                (reset! position* duration)
                                (re/dispatch [:asr-training/audio-ended product k])))
    (.resume ctx)
    (.start source 0 (-> start (/ 1000) (+ offset)) (- duration offset))
    (re-anchor! offset speed)
    (reset! audio-source* source)
    (reset! position* offset)
    ((fn tick [prev-position]
       (when (= source @audio-source*)
         (let [position (current-position utterance)]
           (swap! listened* + (max 0 (- position prev-position)))
           (reset! position* position)
           (js/requestAnimationFrame #(tick position)))))
     offset)))

(re/reg-sub
  :asr-training/list-
  (fn [db [_ product]]
    (let [current-list (get-in db (list-path product))]
      (if (nil? current-list)
        (re/dispatch-load [:asr-training/load-utterances [nil {:product product
                                                               :page 0}]])
        current-list))))

(defn loaded-utterances [list-data]
  (if (or (util/loading? list-data) (some-> (when (map? list-data) (util/loading? (get list-data 0)))))
    :loading
    (some->> list-data
             (into (sorted-map))
             (remove #(util/loading? (last %)))
             (mapcat last)
             (vec))))

(re/reg-sub
  :asr-training/completed-
  (fn [db [_ product]]
    (get-in db (completed-path product))))

(re/reg-sub
  :asr-training/utterances-
  (fn [[_ product]]
    [(re/subscribe [:asr-training/list- product])
     (re/subscribe [:asr-training/completed- product])])
  (fn [[list-data completed]]
    (let [utterances (loaded-utterances list-data)]
      (if (util/loading? utterances)
        utterances
        (filterv #(not (contains? completed (utterance-key %))) utterances)))))

(re/reg-event-db
  :asr-training/reload re/middleware
  (fn [db [product]]
    (clear-playback!)
    (-> db
        (assoc-in (list-path product) nil)
        (assoc-in (audio-path product) nil))))

(re/reg-event-fx
  :asr-training/initialize re/middleware
  (fn [{:keys [db]} [product]]
    (re/dispatch [:core/initialize-filters])
    (re/dispatch [:so-filters/initialize (filters-config product)])
    (sync-panel! product (mode db))
    (re/dispatch [:asr-training/reload product])
    {:db db
     :forward-events {:register :asr-training-initialization-listener
                      :events #{:concepts/select :locations/select :core/select-timeframe
                                :locations/clear-selected :core/timeframe-date-range-changed}
                      :dispatch-to [:asr-training/reload product]}}))

(re/reg-sub
  :asr-training/edits-
  (fn [db [_ product]]
    (get-in db (edits-path product))))

(re/reg-sub
  :asr-training/edit-
  (fn [[_ product]]
    (re/subscribe [:asr-training/edits- product]))
  (fn [edits [_ _ k]]
    (get edits k)))

(defn set-edit [db product {:keys [transcript] :as utterance} v]
  (let [k (utterance-key utterance)]
    (if (= v (default-transcript db product transcript))
      (update-in db (edits-path product) dissoc k)
      (assoc-in db (conj (edits-path product) k) v))))

(re/reg-event-db
  :asr-training/edit-utterance re/middleware
  (fn [db [product utterance v]]
    (set-edit db product utterance v)))

(re/reg-event-db
  :asr-training/reset-utterance re/middleware
  (fn [db [product utterance]]
    (-> db
        (set-edit product utterance (:transcript utterance))
        (update-in (conj root-path product :reset-count) (fnil inc 0)))))

(re/reg-sub
  :asr-training/reset-count-
  (fn [db [_ product]]
    (get-in db (conj root-path product :reset-count) 0)))

(re/reg-sub
  :asr-training/audio-
  (fn [db [_ product]]
    (get-in db (audio-path product))))

(defn selected-key [db product]
  (some-> db (get-in (audio-path product)) :utterance (utterance-key)))

(re/reg-event-db
  :asr-training/select-utterance re/middleware
  (fn [db [product utterance paused?]]
    (clear-playback!)
    (-> (conversation-audio-buffer db product utterance)
        (.then #(re/dispatch [:asr-training/audio-loaded product utterance % paused?]))
        (.catch #(re/dispatch [:asr-training/audio-failed product utterance]))
        (.finally #(re/dispatch [:asr-training/prefetch-audio product (utterance-key utterance)])))
    (assoc-in db (audio-path product) {:utterance utterance
                                       :loading? true})))

(re/reg-event-db
  :asr-training/audio-loaded re/middleware
  (fn [db [product utterance buffer paused?]]
    (if (= (selected-key db product) (utterance-key utterance))
      (do
        (reset! audio-buffer* buffer)
        (when-not paused?
          (play-left-channel! product utterance 0 (playback-speed db)))
        (update-in db (audio-path product) assoc :loading? false :playing? (not paused?)))
      db)))

(re/reg-event-db
  :asr-training/audio-failed re/middleware
  (fn [db [product utterance]]
    (cond-> db
            (= (selected-key db product) (utterance-key utterance))
            (update-in (audio-path product) assoc :loading? false :error? true))))

(re/reg-event-db
  :asr-training/audio-ended re/middleware
  (fn [db [product k]]
    (cond-> db
            (= (selected-key db product) k) (assoc-in (conj (audio-path product) :playing?) false))))

(defn play [db product]
  (play-left-channel! product (get-in db (conj (audio-path product) :utterance)) @position* (playback-speed db))
  (assoc-in db (conj (audio-path product) :playing?) true))

(re/reg-event-db
  :asr-training/toggle-play re/middleware
  (fn [db [product]]
    (let [{:keys [utterance playing?]} (get-in db (audio-path product))]
      (cond
        (not @audio-buffer*) db
        playing? (do
                   (pause-audio! utterance)
                   (assoc-in db (conj (audio-path product) :playing?) false))
        :else (play db product)))))

(re/reg-event-db
  :asr-training/play re/middleware
  (fn [db [product]]
    (if (or (not @audio-buffer*) (get-in db (conj (audio-path product) :playing?)))
      db
      (play db product))))

(re/reg-event-db
  :asr-training/seek re/middleware
  (fn [db [product offset]]
    (let [{:keys [utterance playing?]} (get-in db (audio-path product))]
      (if (and playing? @audio-buffer*)
        (play-left-channel! product utterance offset (playback-speed db))
        (reset! position* offset))
      db)))

(defn apply-playback-speed! [db product speed]
  (when-let [^js source @audio-source*]
    (re-anchor! (current-position (get-in db (conj (audio-path product) :utterance))) speed)
    (set! (.. source -playbackRate -value) speed)))

(re/reg-event-fx
  :asr-training/set-playback-speed re/middleware
  (fn [{:keys [db local-storage]} [product speed]]
    (apply-playback-speed! db product speed)
    {:local-storage (assoc-in local-storage playback-speed-path speed)}))

(re/reg-event-fx
  :asr-training/save-settings re/middleware
  (fn [{:keys [db local-storage]} [product]]
    (let [{:keys [auto-play? hide-original-transcript?] speed :playback-speed} (get-in db settings-form-path)]
      (apply-playback-speed! db product speed)
      (re/dispatch [:dialog/hide])
      {:local-storage (-> local-storage
                          (assoc-in playback-speed-path speed)
                          (assoc-in auto-play-path auto-play?)
                          (assoc-in hide-original-transcript-path hide-original-transcript?))})))

(re/reg-event-db
  :asr-training/teardown re/middleware
  (fn [db [product]]
    (clear-playback!)
    (re/dispatch [:layout/hide-panel-right])
    (assoc-in db (audio-path product) nil)))

(re/reg-sub
  :asr-training/submissions-
  (fn [db [_ product]]
    (get-in db (submissions-path product))))

(re/auth0-http-request
  :asr-training/save-submission
  (fn [{:keys [product key]} _]
    (conj root-path product :submission-requests key))
  (fn [db {:keys [product conversation-id start transcript]}]
    {:method :post
     :url "/api/asr-training/utterance/submit"
     :json-params {:concept-id (concepts.d/current-concept-id db)
                   :product product
                   :conversation-id conversation-id
                   :start start
                   :transcript transcript}}))

(defn can-submit? [db product utterance]
  (or (not (listening-required?))
      (and (= (selected-key db product) (utterance-key utterance))
           (or (listened-enough? utterance)
               (get-in db (conj (audio-path product) :error?))))))

(defn complete-utterance [db product utterance]
  (let [k (utterance-key utterance)
        selected? (= k (selected-key db product))
        completed (-> db (get-in (completed-path product)) (or #{}) (conj k))
        utterances (some->> (get-in db (list-path product))
                            (loaded-utterances)
                            (util/when-not-loading))
        next-utterance (when selected?
                         (some->> utterances
                                  (drop-while #(not= k (utterance-key %)))
                                  (rest)
                                  (filter :audio-url)
                                  (remove #(contains? completed (utterance-key %)))
                                  (first)))]
    (cond
      next-utterance (re/dispatch [:asr-training/select-utterance product next-utterance
                                   (and (= list-mode (mode db)) (not (auto-play? db)))])
      selected? (clear-playback!))
    (when (and (has-more? db product)
               (->> utterances (remove #(contains? completed (utterance-key %))) (count) (>= 75)))
      (re/dispatch [:asr-training/load-utterances [nil {:product product
                                                        :page (next-page db product)}]]))
    (cond-> (-> db
                (assoc-in (completed-path product) completed)
                (update-in (edits-path product) dissoc k))
            (and selected? (not next-utterance)) (assoc-in (audio-path product) nil))))

(re/reg-event-db
  :asr-training/submit-utterance re/middleware
  (fn [db [product {:keys [conversation-id start transcript] :as utterance}]]
    (if-not (can-submit? db product utterance)
      db
      (let [k (utterance-key utterance)
            submission {:conversation-id conversation-id
                        :start start
                        :transcript (get-in db (conj (edits-path product) k) (default-transcript db product transcript))}]
        (re/dispatch [:asr-training/save-submission (assoc submission
                                                      :product product
                                                      :key k)])
        (-> db
            (assoc-in (conj (submissions-path product) k) submission)
            (complete-utterance product utterance))))))

(re/reg-event-db
  :asr-training/skip-utterance re/middleware
  (fn [db [product utterance]]
    (complete-utterance db product utterance)))

(re/reg-event-db
  :asr-training/prefetch-audio re/middleware
  (fn [db [product from-key]]
    (let [completed (get-in db (completed-path product) #{})
          remaining (some->> (get-in db (list-path product))
                             (loaded-utterances)
                             (util/when-not-loading)
                             (filter :audio-url)
                             (remove #(contains? completed (utterance-key %))))
          upcoming (cond->> remaining
                            from-key (drop-while #(not= from-key (utterance-key %))))
          first-by-conversation (->> upcoming
                                     (reverse)
                                     (map (juxt :conversation-id identity))
                                     (into {}))]
      (swap! audio-buffers* select-keys (map :conversation-id remaining))
      (doseq [conversation-id (->> upcoming (map :conversation-id) (distinct) (take 3))]
        (conversation-audio-buffer db product (get first-by-conversation conversation-id)))
      db)))
